import type { TranslationKey } from '../i18n/en'
import type { Lot } from '../types/lot'
import type { MortalityLog } from '../types/production'
import { farmDayKey, farmTimeZone } from './farmTime'
import { isoWeekOf } from './mortalityRecording'

// Findings #140 — "Mortalidad por causa". Crew Mode stores the observed
// cause of each mortality record as a code in `metadata.observedCause`
// (mortality v0 PRD §3; a soft contract: absent or unknown codes must be
// tolerated). GLOBALG.A.P. AQ-GFS 20.05 and ASC ask for mortalities recorded
// with their probable cause and reviewed for patterns; this makes the cause
// usable in Reportes and the Panel. Pure: one row per lot, ISO week (farm
// day) and cause, with the deaths and their share of that lot-week's deaths.
// "Sin mortalidad" zeros and "No se pudo revisar" records carry no deaths
// and are left out; a record with no (or an unknown) cause counts as
// "Sin causa".

export const MORTALITY_CAUSE_LABEL_KEY: Record<string, TranslationKey> = {
  predation: 'crewMode.mortality.cause.predation',
  disease: 'crewMode.mortality.cause.disease',
  handling: 'crewMode.mortality.cause.handling',
  water_quality: 'crewMode.mortality.cause.waterQuality',
  unknown: 'crewMode.mortality.cause.unknown',
  dead_egg: 'crewMode.mortality.cause.deadEgg',
  not_hatched: 'crewMode.mortality.cause.notHatched',
  deformed: 'crewMode.mortality.cause.deformed',
  fungus: 'crewMode.mortality.cause.fungus'
}

export const NO_CAUSE_LABEL_KEY: TranslationKey = 'mortality.cause.none'

/** The record's cause code, or null when it has none we know. */
export const readMortalityCause = (log: Pick<MortalityLog, 'metadata'>): string | null => {
  const code = (log.metadata as Record<string, unknown> | undefined)?.observedCause
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(MORTALITY_CAUSE_LABEL_KEY, code) ? code : null
}

export type MortalityByCauseRow = {
  id: string
  date: string
  week: string
  farmId: string
  productionUnitId: string
  lotId: string
  lot: string
  causeCode: string
  cause: string
  deaths: number
  weekDeaths: number
  causeSharePct: number
}

const dayKeyToDate = (key: string): Date => {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export const buildMortalityByCauseRows = (args: {
  lots: ReadonlyArray<Pick<Lot, 'id' | 'lotKey' | 'farmId' | 'productionUnitId'>>
  mortalityLogs: ReadonlyArray<MortalityLog>
  causeLabel: (key: TranslationKey) => string
}): MortalityByCauseRow[] => {
  const lotById = new Map(args.lots.map((lot) => [lot.id, lot] as const))
  const byKey = new Map<string, MortalityByCauseRow>()
  const weekTotals = new Map<string, number>()

  for (const log of args.mortalityLogs) {
    const metadata = (log.metadata ?? {}) as Record<string, unknown>
    if (metadata.notChecked === true || metadata.noMortality === true) continue
    const deaths = Number.parseFloat(String(log.mortalityCount ?? ''))
    if (!Number.isFinite(deaths) || deaths <= 0) continue
    const lotId = String(log.lotId ?? '').trim()
    const lot = lotId ? lotById.get(lotId) : undefined
    // Records of a lot that no longer exists (deleted) stay out of the report.
    if (lotId && !lot) continue
    const farmId = lot?.farmId ?? log.farmId
    const day = farmDayKey(log.recordedAt, farmTimeZone(farmId))
    if (!day) continue
    const week = isoWeekOf(dayKeyToDate(day))
    const lotKey = lotId || `label:${String(log.lot ?? '').trim()}`
    const causeCode = readMortalityCause(log) ?? ''
    const id = `${lotKey}:${week}:${causeCode || 'none'}`
    const weekKey = `${lotKey}:${week}`
    weekTotals.set(weekKey, (weekTotals.get(weekKey) ?? 0) + deaths)

    const existing = byKey.get(id)
    if (existing) {
      existing.deaths += deaths
      if (day > existing.date) existing.date = day
      continue
    }
    byKey.set(id, {
      id,
      date: day,
      week,
      farmId,
      productionUnitId: lot?.productionUnitId ?? log.productionUnitId,
      lotId,
      lot: lot?.lotKey ?? String(log.lot ?? ''),
      causeCode,
      cause: args.causeLabel(causeCode ? MORTALITY_CAUSE_LABEL_KEY[causeCode] : NO_CAUSE_LABEL_KEY),
      deaths,
      weekDeaths: 0,
      causeSharePct: 0
    })
  }

  const rows = Array.from(byKey.values())
  for (const row of rows) {
    const total = weekTotals.get(`${row.lotId || `label:${row.lot.trim()}`}:${row.week}`) ?? 0
    row.weekDeaths = total
    row.causeSharePct = total > 0 ? Math.round((row.deaths / total) * 1000) / 10 : 0
  }
  return rows.sort((a, b) => (
    b.week.localeCompare(a.week) || a.lot.localeCompare(b.lot) || b.deaths - a.deaths
  ))
}

/** "Manipulación 4 · Sin causa 2" for a set of mortality records (Biomasa). */
export const describeMortalityCauses = (
  logs: ReadonlyArray<Pick<MortalityLog, 'mortalityCount' | 'metadata'>>,
  causeLabel: (key: TranslationKey) => string
): string => {
  const totals = new Map<string, number>()
  for (const log of logs) {
    const deaths = Number.parseFloat(String(log.mortalityCount ?? ''))
    if (!Number.isFinite(deaths) || deaths <= 0) continue
    const code = readMortalityCause(log) ?? ''
    totals.set(code, (totals.get(code) ?? 0) + deaths)
  }
  return Array.from(totals.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([code, deaths]) => `${causeLabel(code ? MORTALITY_CAUSE_LABEL_KEY[code] : NO_CAUSE_LABEL_KEY)} ${deaths}`)
    .join(' · ')
}
